import { useCustomFields } from '@/hooks/useCustomFields';
import { Input, Select } from '@solid/ui';
import type { CustomFieldEntityType, CustomFieldDefinition } from '@/types';

interface CustomFieldsFormProps {
  entityType: CustomFieldEntityType;
  currentValues: Record<string, any>;
  onChange: (values: Record<string, any>) => void;
}

export function CustomFieldsForm({ entityType, currentValues, onChange }: CustomFieldsFormProps) {
  const { data: definitions } = useCustomFields(entityType);

  if (!definitions?.length) return null;

  const activeFields = definitions.filter((d) => d.isActive);
  if (!activeFields.length) return null;

  const handleFieldChange = (name: string, value: any) => {
    onChange({ ...currentValues, [name]: value });
  };

  return (
    <div className="space-y-3">
      <p className="text-xs font-medium text-grey-500 uppercase tracking-wider">Custom Fields</p>
      {activeFields.map((field) => (
        <FieldInput
          key={field.id}
          field={field}
          value={currentValues[field.name]}
          onChange={(value) => handleFieldChange(field.name, value)}
        />
      ))}
    </div>
  );
}

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: CustomFieldDefinition;
  value: any;
  onChange: (value: any) => void;
}) {
  switch (field.fieldType) {
    case 'TEXT':
      return (
        <Input
          label={field.label}
          required={field.isRequired}
          placeholder={field.description || undefined}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'NUMBER':
      return (
        <Input
          label={field.label}
          type="number"
          required={field.isRequired}
          placeholder={field.description || undefined}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
        />
      );

    case 'DATE':
      return (
        <Input
          label={field.label}
          type="date"
          required={field.isRequired}
          value={value ? String(value).split('T')[0] : ''}
          onChange={(e) => onChange(e.target.value || undefined)}
        />
      );

    case 'SELECT':
      return (
        <Select
          label={field.label}
          required={field.isRequired}
          value={value || ''}
          onChange={(e) => onChange(e.target.value || undefined)}
          options={[
            { value: '', label: 'Select...' },
            ...(Array.isArray(field.options) ? field.options.map((opt) => ({ value: opt, label: opt })) : []),
          ]}
        />
      );

    case 'BOOLEAN':
      return (
        <label className="flex items-center gap-2 text-sm text-grey-700">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="rounded border-grey-300"
          />
          {field.label}
          {field.isRequired && <span className="text-error">*</span>}
        </label>
      );

    case 'URL':
      return (
        <Input
          label={field.label}
          type="url"
          required={field.isRequired}
          placeholder={field.description || 'https://...'}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    default:
      return null;
  }
}
